import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Crown, Sparkles, HardDrive, Loader2, Zap, ShieldCheck, Tag, ExternalLink, BadgeCheck, XCircle, PauseCircle, PlayCircle, AlertTriangle, CreditCard } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getStoredPromo } from "@/lib/promos";
import { cn } from "@/lib/utils";

const PRO_FEATURES = [
    "Live AI Tactical Coach", "Post-Match Reviews & Coaching", "Career Mode & Road To Pro",
    "Aim / Positioning / Awareness Analysis", "Auto Clip Generation", "AI Scout (best weapon/map)",
    "Encounter & Anomaly Analysis", "Evidence Packages", "Analytics Dashboard", "100 GB Cloud Storage",
];
const CREATOR_EXTRA = [
    "Everything in Pro", "Content Studio & Highlight Reels", "AI Titles, Descriptions & Hashtags",
    "TikTok / X / Instagram Captions", "Narration & Commentary Scripts", "Family-Friendly Export Mode",
    "Priority AI Processing", "500 GB Cloud Storage",
];

export default function Pricing() {
    const { user, refresh } = useAuth();
    const [cat, setCat] = useState(null);
    const [annual, setAnnual] = useState(false);
    const [busy, setBusy] = useState("");

    useEffect(() => { api.get("/billing/catalog").then(({ data }) => setCat(data)); }, []);

    const manageSub = async (action) => {
        setBusy(action);
        try {
            await api.post(`/billing/${action}`);
            const msg = action === "cancel" ? "Subscription will end at the close of your billing cycle."
                : action === "freeze" ? "Billing paused. Resume anytime."
                : "Subscription resumed.";
            toast.success(msg);
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Action failed. Please try again.");
        } finally { setBusy(""); }
    };

    const checkout = async (planKey) => {
        setBusy(planKey);
        try {
            const promo_code = getStoredPromo() || undefined;
            const { data } = await api.post("/billing/checkout", { plan_key: planKey, origin: window.location.origin, promo_code });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start checkout.");
            setBusy("");
        }
    };

    if (!cat) return <Loader label="Loading plans" />;

    const price = (tier) => {
        const key = `${tier}_${annual ? "annual" : "monthly"}`;
        return cat.plans.find((p) => p.key === key);
    };
    const pro = price("pro"), creator = price("creator");
    const ent = user?.entitlements || {};
    const plan = ent.plan || "free";
    const isOwner = user?.is_owner || plan === "owner";

    const portal = async () => {
        try {
            const { data } = await api.post("/billing/portal", { origin: window.location.origin });
            window.location.href = data.url;
        } catch (err) { toast.error(err.response?.data?.detail || "Could not open billing portal."); }
    };

    const addCard = async () => {
        setBusy("add-card");
        try {
            const { data } = await api.post("/billing/setup-card", { origin: window.location.origin });
            window.location.href = data.url;
        } catch (err) { toast.error(err.response?.data?.detail || "Could not open card setup."); setBusy(""); }
    };

    const isTrialing = ent.subscription_status === "trialing";
    const needsCard = isTrialing && !ent.has_payment_method && !isOwner;
    const trialEnd = ent.trial_end ? new Date(ent.trial_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

    const PLAN_NAME = { free: "Free", pro: "WhiteWater Pro", creator: "WhiteWater Creator", owner: "Owner (All Access)" };
    const paid = plan === "pro" || plan === "creator";
    const periodEnd = ent.current_period_end
        ? new Date(ent.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
        : "";

    return (
        <div className="ww-fade-up" data-testid="pricing-page">
            <PageHeader title="Plan Management" subtitle="Your subscription, plan options and storage add-ons." />

            {needsCard && (
                <div className="mb-6 rounded-sm border border-volt/40 bg-volt/[0.07] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="trial-add-card-banner">
                    <CreditCard className="w-5 h-5 text-volt shrink-0" />
                    <div className="flex-1">
                        <div className="text-white font-semibold text-sm">You're on a free Pro trial{trialEnd ? ` until ${trialEnd}` : ""}.</div>
                        <div className="text-xs text-muted-foreground">Add a card to keep Pro after your trial — no charge until it ends, cancel anytime.</div>
                    </div>
                    <button onClick={addCard} disabled={busy === "add-card"} data-testid="add-card-button"
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy === "add-card" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Add Payment Method
                    </button>
                </div>
            )}

            {/* Current plan status */}
            <div className="mb-6 rounded-sm border border-border bg-surface p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="current-plan-card">
                <div className={cn("w-11 h-11 rounded-sm grid place-items-center shrink-0",
                    plan === "free" ? "bg-white/5 border border-border" : "bg-volt/15 border border-volt/40")}>
                    {isOwner ? <Crown className="w-5 h-5 text-volt" /> : plan === "creator" ? <Crown className="w-5 h-5 text-volt" /> : plan === "pro" ? <Zap className="w-5 h-5 text-volt" /> : <BadgeCheck className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Current Plan</div>
                    <div className="text-white font-heading font-bold text-lg" data-testid="current-plan-name">{PLAN_NAME[plan] || "Free"}
                        {ent.plan_interval && <span className="text-xs text-muted-foreground font-mono ml-2">/ {ent.plan_interval}</span>}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                        {isOwner ? "Comped — all features unlocked, forever" :
                         ent.subscription_paused ? "Billing paused — resume anytime" :
                         ent.cancel_at_period_end ? `Cancels at period end${periodEnd ? ` · ends ${periodEnd}` : ""}` :
                         ent.subscription_status === "trialing" ? "7-day free trial active" :
                         ent.subscription_status ? ent.subscription_status.replace("_", " ") :
                         plan === "free" ? "No active subscription" : "Active"}
                    </div>
                </div>
                {!isOwner && paid && (
                    <div className="flex flex-wrap items-center gap-2">
                        {(ent.cancel_at_period_end || ent.subscription_paused) ? (
                            <button onClick={() => manageSub("resume")} disabled={!!busy} data-testid="resume-subscription"
                                className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60 transition-colors">
                                {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Resume
                            </button>
                        ) : (
                            <>
                                <ConfirmAction testid="freeze-subscription" disabled={!!busy}
                                    trigger={<><PauseCircle className="w-4 h-4" /> Freeze</>}
                                    triggerClass="border border-border text-white hover:border-white/40"
                                    title="Freeze your subscription?"
                                    desc="Billing pauses immediately (no further charges) and you can resume anytime. Your plan stays on your account."
                                    confirmLabel="Freeze billing" onConfirm={() => manageSub("freeze")} />
                                <ConfirmAction testid="cancel-subscription" disabled={!!busy}
                                    trigger={<><XCircle className="w-4 h-4" /> Cancel</>}
                                    triggerClass="border border-alert/40 text-alert hover:bg-alert/10"
                                    title="Cancel your subscription?"
                                    desc="Your plan stays active until the end of the current billing cycle, then drops to Free. You won't be charged again. You can resume before it ends."
                                    confirmLabel="Cancel at period end" onConfirm={() => manageSub("cancel")} />
                            </>
                        )}
                        <button onClick={portal} data-testid="manage-billing-pricing"
                            className="h-10 px-5 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center gap-2">
                            <ExternalLink className="w-4 h-4" /> Manage Billing
                        </button>
                    </div>
                )}
            </div>

            {isOwner ? (
                <div className="rounded-sm border border-volt/40 bg-gradient-to-r from-volt/[0.1] to-transparent p-6 text-center" data-testid="owner-all-access">
                    <Crown className="w-8 h-8 text-volt mx-auto mb-2" />
                    <h3 className="font-heading text-xl font-bold mb-1">All Access — Owner</h3>
                    <p className="text-sm text-muted-foreground">Every feature is unlocked on your account. No subscription needed.</p>
                </div>
            ) : (
            <>
            {/* Trial banner */}
            <div className="mb-6 rounded-sm border border-volt/40 bg-gradient-to-r from-volt/[0.1] to-transparent p-4 flex items-center gap-3" data-testid="trial-banner">
                <ShieldCheck className="w-5 h-5 text-volt shrink-0" />
                <p className="text-sm text-muted-foreground">
                    <span className="text-white font-semibold">7-Day Free Trial</span> — card required, $0 for 7 days, then auto-converts to your selected plan. Cancel anytime.
                </p>
            </div>

            {/* Billing interval toggle */}
            <div className="flex items-center justify-center gap-3 mb-8" data-testid="interval-toggle">
                <button onClick={() => setAnnual(false)} className={cn("text-sm font-semibold transition-colors", !annual ? "text-white" : "text-muted-foreground")}>Monthly</button>
                <button onClick={() => setAnnual(!annual)} data-testid="toggle-annual"
                    className="relative w-12 h-6 rounded-full bg-obsidian border border-border">
                    <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-volt transition-all", annual ? "left-[26px]" : "left-1")} />
                </button>
                <button onClick={() => setAnnual(true)} className={cn("text-sm font-semibold transition-colors flex items-center gap-1.5", annual ? "text-white" : "text-muted-foreground")}>
                    Annual <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-success/15 text-success border border-success/30">SAVE ~17%</span>
                </button>
            </div>

            {/* Plan cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Pro */}
                <div className={cn("relative rounded-sm border bg-surface p-7", plan === "pro" ? "border-volt" : "border-border")} data-testid="plan-pro">
                    {plan === "pro" && <span className="absolute top-4 right-4 text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm bg-volt/15 text-volt border border-volt/40">Current</span>}
                    <div className="flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-volt" /><h3 className="font-heading text-xl font-bold">WhiteWater Pro</h3></div>
                    <p className="text-sm text-muted-foreground mb-4">For competitive players & ranked grinders.</p>
                    <div className="mb-5">
                        <span className="font-heading text-4xl font-bold text-white">${annual ? "149" : "14.99"}</span>
                        <span className="text-muted-foreground text-sm">/{annual ? "year" : "month"}</span>
                        {annual && <div className="text-xs text-success mt-1">Save $30.88 vs monthly</div>}
                    </div>
                    <button onClick={() => checkout(pro.key)} disabled={!!busy} data-testid="subscribe-pro"
                        className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy === pro.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start 7-Day Free Trial
                    </button>
                    <ul className="mt-6 space-y-2.5">
                        {PRO_FEATURES.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-success mt-0.5 shrink-0" />{f}</li>)}
                    </ul>
                </div>

                {/* Creator */}
                <div className={cn("relative rounded-sm border-2 bg-surface p-7", plan === "creator" ? "border-volt" : "border-volt/50")} data-testid="plan-creator">
                    <span className="absolute -top-3 left-7 text-[10px] font-mono uppercase px-2.5 py-1 rounded-sm bg-volt text-black font-bold">Most Popular</span>
                    {plan === "creator" && <span className="absolute top-4 right-4 text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm bg-volt/15 text-volt border border-volt/40">Current</span>}
                    <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-volt" /><h3 className="font-heading text-xl font-bold">WhiteWater Creator</h3></div>
                    <p className="text-sm text-muted-foreground mb-4">For streamers, YouTubers & content creators.</p>
                    <div className="mb-5">
                        <span className="font-heading text-4xl font-bold text-white">${annual ? "249" : "24.99"}</span>
                        <span className="text-muted-foreground text-sm">/{annual ? "year" : "month"}</span>
                        {annual && <div className="text-xs text-success mt-1">Save $50.88 vs monthly</div>}
                    </div>
                    <button onClick={() => checkout(creator.key)} disabled={!!busy} data-testid="subscribe-creator"
                        className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy === creator.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start 7-Day Free Trial
                    </button>
                    <ul className="mt-6 space-y-2.5">
                        {CREATOR_EXTRA.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-success mt-0.5 shrink-0" />{f}</li>)}
                    </ul>
                </div>
            </div>

            {/* Storage add-ons */}
            <div className="max-w-4xl mx-auto mt-10">
                <h3 className="font-heading text-lg font-bold mb-1 flex items-center gap-2"><HardDrive className="w-5 h-5 text-volt" /> Storage Add-Ons</h3>
                <p className="text-sm text-muted-foreground mb-4">Stack extra cloud storage on top of your plan. Billed monthly.</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {cat.storage.map((s) => (
                        <div key={s.key} className="rounded-sm border border-border bg-surface p-4 flex flex-col" data-testid={`storage-${s.key}`}>
                            <div className="font-heading font-bold text-white">{s.gb >= 1024 ? `+${s.gb / 1024} TB` : `+${s.gb} GB`}</div>
                            <div className="text-2xl font-heading font-bold text-volt my-2">${s.amount}<span className="text-xs text-muted-foreground font-sans">/mo</span></div>
                            <button onClick={() => checkout(s.key)} disabled={!!busy} data-testid={`buy-${s.key}`}
                                className="mt-auto h-9 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                                {busy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <p className="text-center text-xs text-muted-foreground mt-8 flex items-center justify-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Have a promo code? Apply it at checkout. Secure payments by Stripe.
            </p>
            </>
            )}
        </div>
    );
}

function ConfirmAction({ trigger, triggerClass, title, desc, confirmLabel, onConfirm, testid, disabled }) {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <button disabled={disabled} data-testid={testid}
                    className={cn("h-10 px-5 rounded-sm font-semibold flex items-center gap-2 disabled:opacity-60 transition-colors", triggerClass)}>
                    {trigger}
                </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-surface border-border">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-warn" /> {title}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{desc}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`${testid}-dismiss`} className="bg-transparent border-border">Keep my plan</AlertDialogCancel>
                    <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-volt text-black hover:bg-[#33ceff]">{confirmLabel}</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
